import { getAccessToken, API_ENDPOINTS } from '../api';

/**
 * Uploads a file to S3 using presigned URL (direct upload — bypasses API Gateway).
 * Step 1: Get presigned URL from backend
 * Step 2: PUT file directly to S3 using presigned URL
 *
 * @param {File} file - The file to upload
 * @param {string} folder - Destination folder (e.g. 'offers', 'blogs', 'featured')
 * @returns {Promise<string>} - The public image URL to store in DynamoDB
 */
export async function uploadFileToS3(file, folder = 'offers') {
  // Step 1 — Get presigned URL from backend
  const params = new URLSearchParams({
    folder,
    fileName: file.name,
    contentType: file.type || 'image/jpeg',
  });

  const presignedResponse = await fetch(`${API_ENDPOINTS.S3_PRESIGNED_URL}?${params}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${getAccessToken()}`,
    },
  });

  if (!presignedResponse.ok) {
    throw new Error(`Failed to get upload URL (${presignedResponse.status})`);
  }

  const { uploadUrl, imageUrl } = await presignedResponse.json();

  // Step 2 — Upload file directly to S3 (no auth header, raw binary)
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'image/jpeg',
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload image to S3 (${uploadResponse.status})`);
  }

  return imageUrl;
}
